import { useState, useRef, useEffect } from 'react';
import { Message, ChatMessage } from '../types';
import { handleMessageStop } from '../utils/messageUtils';
import { scrollToBottom } from '../utils/chatUtils';
import { sendChatMessage, getChatDetail, resendChatMessage, getUsageStats, submitMessageFeedback, RateLimitError, UsageInfo } from '../utils/chatApi';

interface MessageCallbacks {
  onChatIdReceived?: (chatId: string) => void;
  onFinished?: () => void;
}

interface UseMessagesOptions {
  onConversationUpdated?: () => void;
  onMessageForInactiveChat?: (chatId: string) => void;
}

export interface UsageState {
  limit: number;
  used: number;
  remaining: number;
  isUnlimited: boolean;
  limitExceeded: boolean;
  resetsAt: string | null;
}

const DEFAULT_USAGE_STATE: UsageState = {
  limit: 30,
  used: 0,
  remaining: 30,
  isUnlimited: false,
  limitExceeded: false,
  resetsAt: null,
};

export const useMessages = (initialChatId?: string | null, options?: UseMessagesOptions) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingContent, setEditingContent] = useState<string>('');
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [currentChatId, setCurrentChatId] = useState<string | null>(initialChatId || null);
  const [usageInfo, setUsageInfo] = useState<UsageState>(DEFAULT_USAGE_STATE);
  const abortControllerRef = useRef<AbortController | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const viewVersionRef = useRef(0);
  const pendingRequestVersionRef = useRef<number | null>(null);

  // Fetch initial usage stats
  useEffect(() => {
    const fetchUsageStats = async () => {
      try {
        const response = await getUsageStats();
        if (response.success && response.data) {
          setUsageInfo({
            limit: response.data.daily_limit,
            used: response.data.used_today,
            remaining: response.data.remaining_today,
            isUnlimited: response.data.is_unlimited,
            limitExceeded: response.data.remaining_today <= 0 && !response.data.is_unlimited,
            resetsAt: response.data.resets_at || null,
          });
        }
      } catch (error) {
        console.error('Error fetching usage stats:', error);
      }
    };

    fetchUsageStats();
  }, []);

  const updateUsageFromResponse = (usage: UsageInfo | undefined) => {
    if (usage) {
      setUsageInfo(prev => ({
        ...prev,
        limit: usage.limit,
        used: usage.used,
        remaining: usage.remaining,
        limitExceeded: usage.remaining <= 0 && !prev.isUnlimited,
        resetsAt: usage.resets_at || prev.resetsAt,
      }));
    }
  };

  const handleRateLimitError = (error: RateLimitError) => {
    setUsageInfo(prev => ({
      ...prev,
      limit: error.usage.limit,
      used: error.usage.used,
      remaining: 0,
      limitExceeded: true,
      resetsAt: error.usage.resets_at || prev.resetsAt,
    }));
  };

  const updatePendingUserMessage = (update: Partial<Message>) => {
    setMessages(prev => {
      const updated = [...prev];
      for (let i = updated.length - 1; i >= 0; i--) {
        const msg = updated[i];
        if (!msg.isBot && !msg.messageId) {
          updated[i] = { ...msg, ...update };
          break;
        }
      }
      return updated;
    });
  };

  const notifyConversationUpdate = () => {
    options?.onConversationUpdated?.();
  };

  const bumpViewVersion = () => {
    viewVersionRef.current += 1;
    setIsLoading(false);
    return viewVersionRef.current;
  };

  const isCurrentViewVersion = (version: number) => version === viewVersionRef.current;

  const loadChatHistory = async (chatId: string) => {
    const loadVersion = bumpViewVersion();
    // Don't set isLoading here to avoid showing the AI loading bubble
    try {
      const response = await getChatDetail(chatId);
      if (!isCurrentViewVersion(loadVersion)) {
        return;
      }
      // Convert ChatMessage[] to Message[]
      const loadedMessages: Message[] = [];
      response.messages.forEach((msg: ChatMessage) => {
        // User message (question side)
        loadedMessages.push({
          content: msg.question,
          isBot: false,
          messageId: msg.message_id
        });
        // Bot message (answer side) — carry messageId so feedback buttons work,
        // and restore any previously saved feedback
        loadedMessages.push({
          content: msg.answer,
          isBot: true,
          messageId: msg.message_id,
          references: msg.references,
          reask: msg.reask,
          userFeedback: msg.user_feedback ?? null,
        });
      });
      setMessages(loadedMessages);
      setCurrentChatId(chatId);
    } catch (error) {
      console.error('Error loading chat history:', error);
      if (!isCurrentViewVersion(loadVersion)) {
        return;
      }
      setMessages([{
        content: 'Failed to load chat history.',
        isBot: true,
        error: true
      }]);
    }
  };

  // Load chat history when chatId changes
  useEffect(() => {
    if (initialChatId) {
      loadChatHistory(initialChatId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialChatId]);

  const handleStop = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      setIsLoading(false);
      setMessages(prev => handleMessageStop(prev));
    }
  };

  const clearMessages = () => {
    // Stop any ongoing requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    bumpViewVersion();
    // Clear all messages and reset state
    setMessages([]);
    setIsLoading(false);
    setCurrentChatId(null);
    setEditingIndex(null);
    setEditingContent('');
    setEditingMessageId(null);
  };

  const retryMessage = (messageIndex: number) => {
    // Stop any ongoing requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      setIsLoading(false);
    }

    // Find the user message before this bot message
    const userMessageIndex = messageIndex - 1;
    if (userMessageIndex < 0) {
      return;
    }

    const targetMessage = messages[userMessageIndex];

    if (!targetMessage || targetMessage.isBot) {
      return;
    }

    if (!targetMessage.messageId) {
      console.warn('Unable to retry this question because it has not been saved yet.');
      return;
    }

    const questionContent = targetMessage.content;
    const messageId = targetMessage.messageId;

    // Delete all messages from the selected user message onwards and add the question back
    setMessages(prev => [...prev.slice(0, userMessageIndex), { content: questionContent, isBot: false }]);

    // Resend the user message using the history endpoint
    resendHistoryMessage(messageId);
  };

  const simulateBotResponse = async (userMessage: string, callbacks?: MessageCallbacks) => {
    setIsLoading(true);

    abortControllerRef.current = new AbortController();
    const requestVersion = viewVersionRef.current;
    pendingRequestVersionRef.current = requestVersion;

    try {
      const response = await sendChatMessage(userMessage, currentChatId, {
        signal: abortControllerRef.current.signal
      });
      const botMessage = response.message?.answer || 'No response from server';
      const references = response.message?.references;
      const reask = response.message?.reask;
      // Grab the message_id for the bot bubble so feedback buttons work
      const messageId = response.message?.message_id;
      const isActiveView = isCurrentViewVersion(requestVersion);

      // Update usage info from response
      updateUsageFromResponse(response.usage);

      // Update chat_id if this is a new chat
      if (isActiveView && response.chat_id && !currentChatId) {
        setCurrentChatId(response.chat_id);
        callbacks?.onChatIdReceived?.(response.chat_id);
      }

      if (isActiveView && response.message?.message_id) {
        updatePendingUserMessage({ messageId: response.message.message_id });
      }

      if (isActiveView) {
        setMessages(prev => [...prev, {
          content: botMessage,
          isBot: true,
          messageId,           // carry messageId on bot message
          references,
          reask,
          userFeedback: null,  // fresh message, no feedback yet
        }]);
      } else if (response.chat_id) {
        options?.onMessageForInactiveChat?.(response.chat_id);
      }
    } catch (error: unknown) {
      const isActiveView = isCurrentViewVersion(requestVersion);

      // Handle rate limit error
      if (error instanceof RateLimitError) {
        handleRateLimitError(error);
        if (isActiveView) {
          // Remove the pending user message since it wasn't processed
          setMessages(prev => prev.slice(0, -1));
        }
        return;
      }

      if (error instanceof Error && error.name === 'AbortError') {
        if (isActiveView) {
          // Request was aborted by user - show a stopped message bubble
          setMessages(prev => [...prev, {
            content: 'Response generation was stopped.',
            isBot: true,
            stopped: true
          }]);
        }
      } else if (error instanceof Error && error.message !== 'Response stopped by user') {
        console.error('Error getting bot response:', error);
        if (isActiveView) {
          setMessages(prev => [...prev, {
            content: 'Response Error.',
            isBot: true,
            error: true
          }]);
        }
      } else if (isActiveView) {
        // User stopped the response
        setMessages(prev => [...prev, {
          content: 'Cancelled by User.',
          isBot: true,
          stopped: true
        }]);
      }
    } finally {
      if (pendingRequestVersionRef.current === requestVersion) {
        pendingRequestVersionRef.current = null;
      }
      setIsLoading(false);
      abortControllerRef.current = null;
      callbacks?.onFinished?.();
    }
  };

  const addUserMessage = (message: string, callbacks?: MessageCallbacks) => {
    // Check if limit exceeded before adding message
    if (usageInfo.limitExceeded) {
      return;
    }
    setMessages(prev => [...prev, { content: message, isBot: false }]);
    simulateBotResponse(message, callbacks);
  };

  const resendHistoryMessage = async (messageId: string, newMessage?: string) => {
    // Check if limit exceeded
    if (usageInfo.limitExceeded) {
      return;
    }

    setIsLoading(true);

    abortControllerRef.current = new AbortController();
    const requestVersion = viewVersionRef.current;
    pendingRequestVersionRef.current = requestVersion;

    try {
      const response = await resendChatMessage(
        messageId,
        newMessage,
        true,
        abortControllerRef.current.signal
      );
      const botMessage = response.message?.answer || 'No response from server';
      const references = response.message?.references;
      const reask = response.message?.reask;
      const newMessageId = response.message?.message_id;
      const isActiveView = isCurrentViewVersion(requestVersion);

      // Update usage info from response
      updateUsageFromResponse(response.usage);

      if (isActiveView && response.chat_id) {
        setCurrentChatId(response.chat_id);
      }

      if (isActiveView && response.message?.message_id) {
        updatePendingUserMessage({ messageId: response.message.message_id });
      }

      if (isActiveView) {
        setMessages(prev => [...prev, {
          content: botMessage,
          isBot: true,
          messageId: newMessageId,   // carry messageId on bot message
          references,
          reask,
          userFeedback: null,
        }]);
        notifyConversationUpdate();
      } else if (response.chat_id) {
        options?.onMessageForInactiveChat?.(response.chat_id);
      }
    } catch (error: unknown) {
      const isActiveView = isCurrentViewVersion(requestVersion);

      // Handle rate limit error
      if (error instanceof RateLimitError) {
        handleRateLimitError(error);
        if (isActiveView) {
          // Remove the pending user message since it wasn't processed
          setMessages(prev => prev.slice(0, -1));
        }
        return;
      }

      if (error instanceof Error && error.name === 'AbortError') {
        if (isActiveView) {
          setMessages(prev => [...prev, {
            content: 'Response generation was stopped.',
            isBot: true,
            stopped: true
          }]);
        }
      } else if (error instanceof Error && error.message !== 'Response stopped by user') {
        console.error('Error resending message:', error);
        if (isActiveView) {
          setMessages(prev => [...prev, {
            content: error.message || 'Response Error.',
            isBot: true,
            error: true
          }]);
        }
      } else if (isActiveView) {
        setMessages(prev => [...prev, {
          content: 'Cancelled by User.',
          isBot: true,
          stopped: true
        }]);
      }
    } finally {
      setIsLoading(false);
      abortControllerRef.current = null;
      if (pendingRequestVersionRef.current === requestVersion) {
        pendingRequestVersionRef.current = null;
      }
    }
  };

  const editMessage = (messageIndex: number) => {
    // Stop any ongoing requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      setIsLoading(false);
    }

    // Set editing state
    const messageToEdit = messages[messageIndex];
    if (!messageToEdit) {
      return;
    }

    if (!messageToEdit.isBot) {
      if (!messageToEdit.messageId) {
        console.warn('Attempted to edit a question before it was saved on the server.');
        return;
      }
      setEditingIndex(messageIndex);
      setEditingContent(messageToEdit.content);
      setEditingMessageId(messageToEdit.messageId);
    }
  };

  const cancelEdit = () => {
    setEditingIndex(null);
    setEditingContent('');
    setEditingMessageId(null);
  };

  const submitEdit = () => {
    if (editingIndex === null || !editingContent.trim() || !editingMessageId) {
      return;
    }

    // Check if limit exceeded
    if (usageInfo.limitExceeded) {
      cancelEdit();
      return;
    }

    // Delete all messages from the edited message onwards and add the edited message back
    const newContent = editingContent.trim();
    setMessages(prev => [...prev.slice(0, editingIndex), { content: newContent, isBot: false }]);

    // Clear editing state
    setEditingIndex(null);
    setEditingContent('');
    const targetMessageId = editingMessageId;
    setEditingMessageId(null);

    resendHistoryMessage(targetMessageId, newContent);
  };

  // Submit like/dislike feedback for a bot message
  const submitFeedback = async (messageId: string, feedback: 'like' | 'dislike' | null) => {
    // Optimistic update — MessageCard already did this locally,
    // but keep messages state in sync too so it survives re-renders
    setMessages(prev =>
      prev.map(msg =>
        msg.messageId === messageId && msg.isBot
          ? { ...msg, userFeedback: feedback }
          : msg
      )
    );

    try {
      await submitMessageFeedback(messageId, feedback);
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      // Silently fail — the optimistic update stays, not worth reverting UX over this
    }
  };

  useEffect(() => {
    if (messages.length > 0 || isLoading) {
      scrollToBottom(scrollContainerRef.current);
    }
  }, [messages, isLoading]);

  const hasConversation = messages.length > 0 || isLoading;

  return {
    messages,
    isLoading,
    scrollContainerRef,
    handleStop,
    addUserMessage,
    clearMessages,
    hasConversation,
    retryMessage,
    editMessage,
    editingIndex,
    editingContent,
    setEditingContent,
    cancelEdit,
    submitEdit,
    currentChatId,
    loadChatHistory,
    usageInfo,
    submitFeedback,
  };
};